import {
  IWidgetConfigurationProps,
  withWidgetConfiguration,
} from "@sgwt-widget/core";
import { isEqual } from "lodash";
import { h } from "preact";
import { ApiBackedComponent } from "../../common/components/ApiBackedComponent";
import { BuildDateFilter } from "../../common/components/PivotTable/InteractiveFilter";
import {
  GetCommonState,
  getFieldInformations,
  PersistConfigChange,
} from "../../common/components/PivotTable/InteractiveHelpers";
import { InteractiveState } from "../../common/components/PivotTable/pivot-types";
import { PivotTableWithChart } from "../../common/components/PivotTable/PivotTableWithChart";
import { MakeGenericApiCall } from "../../common/generic/generic-api";
import {
  CheckReponseDataForError,
  GenericFilterBuilder,
} from "../../common/generic/generic-helper";
import { SCAttributes } from "../../common/generic/generic-sc-attributes";
import { IGenericRequest } from "../../common/generic/generic-types";
import { ConvertFilter } from "../../common/salescredit/salescredit-filter-help";
import {
  BuildSalesCreditFilters,
  CreateDateFromFilter,
} from "../../common/salescredit/salescredit-search-criteria";
import { IProps } from "../../common/salescredit/salescredit-types";
import { ObsoleteWidgetMessage } from "../../common/utils/ObsoleteWidgetMessage";
import { INewPivotTableOptions } from "../../common/utils/types";
import {
  getAllAttributes,
  getAllKpis,
  GetResponseinCsv,
  GetResponseinJson,
  SymbolText,
} from "../utils/helper";

const YEAR_COL = "Year";
const indicator = "sc";
const SC_PRODUCT_LEVEL2 = "Product Level 2";

export class SalesCreditInteractiveChartClass extends ApiBackedComponent<
  IProps & IWidgetConfigurationProps,
  InteractiveState
> {
  public constructor() {
    super();
    this.setState(
      GetCommonState([YEAR_COL], [SC_PRODUCT_LEVEL2], "SC", "sc_sc")
    );
    this.pivotOptionsChanged = this.pivotOptionsChanged.bind(this);
    this.showHideAdvancedMode = this.showHideAdvancedMode.bind(this);
  }

  public componentWillMount() {
    PersistConfigChange(this.props);
  }

  public componentWillUnmount() {
    PersistConfigChange(this.props);
  }

  protected shouldFetchApiData(nextProps: IProps): boolean {
    return !isEqual(this.props.filters, nextProps.filters);
  }

  protected renderWithDataReady(): JSX.Element {
    const tableHeaders = this.state.attributes.concat(
      this.state.kpis.filter((x: any) => x.name === this.state.aggregatorName)
    );
    return (
      <div>
        <ObsoleteWidgetMessage newWidgetId={2440} />
        <button
          style="margin-left: 10px;"
          class="btn btn-default"
          onClick={this.showHideAdvancedMode}
        >
          <span class="fa fa-cog" /> Show/hide advanced mode
        </button>
        <PivotTableWithChart
          advanceShown={this.state.advanceShown}
          data={this.state.data}
          defaultRows={this.state.rows}
          defaultColumns={this.state.columns}
          tableHeaders={tableHeaders}
          aggregators={this.state.kpis}
          onPivotTableOptionsChanged={this.pivotOptionsChanged}
          renderer={this.state.renderer}
          aggregatorName={this.state.aggregatorName}
          jsonData={this.state.jsonData}
          spinnerClass={this.state.spinnerClass}
          exclusions={this.state.exclusions}
          unitSymbol={SymbolText}
          rowOrder={this.state.rowOrder}
          colOrder={this.state.colOrder}
        />
      </div>
    );
  }

  protected async fetchApiData(props: IProps) {
    this.initCorrelationId();
    const filtersConverted = ConvertFilter(props.filters);
    const filters = BuildSalesCreditFilters(filtersConverted);
    this.setState({ filters });
    this.setDataStatus("loading");
    if (props.userSelection && props.userSelection.config) {
      const config = JSON.parse(props.userSelection.config as string);
      this.setState({
        aggregatorName: this.getChangedAggName(config.aggregatorName),
        colOrder: config.colOrder,
        columns: getFieldInformations(config.cols, "name"),
        exclusions: config.exclusions,
        renderer: config.rendererName,
        rowOrder: config.rowOrder,
        rows: getFieldInformations(config.rows, "name"),
      });
    }

    const attributes = getAllAttributes().map((x: any) => {
      return { id: x.id, name: x.name };
    });

    const kpis = getAllKpis().map((x: any) => {
      return { id: x.id, name: x.name };
    });
    this.setState({
      attributes,
      kpis,
    });
    await this.fetchAndSetState(props);
  }
  private async fetchAndSetState(props: IProps) {
    this.setDataStatus("loading");
    const genericRequest: IGenericRequest = this.ConstructRequest(props);
    const responseData = await MakeGenericApiCall(
      props.headers,
      indicator,
      genericRequest,
      this.props.widgetConfiguration,
      this.state.correlationId
    );
    const errorStatus = CheckReponseDataForError(responseData);
    if (errorStatus) {
      this.setState({
        data: [],
        jsonData: [],
      });
      if (errorStatus !== "no-data") {
        this.setDataStatus(errorStatus);
        this.setStatusText(responseData.statusText);
      } else {
        this.setDataStatus("ready");
      }
    } else {
      const data = GetResponseinCsv(responseData);
      const jsonData = GetResponseinJson(responseData);
      this.setState({
        data,
        jsonData,
      });
      this.setDataStatus("ready");
    }
  }

  private ConstructRequest(props: IProps): IGenericRequest {
    const filtersConverted = ConvertFilter(props.filters);
    const filter = GenericFilterBuilder(this.state.filters);

    const dateFilter = CreateDateFromFilter(filtersConverted);
    const validTimeDimensions = [
      SCAttributes.SC_CALENDAR_YEAR,
      SCAttributes.SC_CALENDAR_QUARTER,
      SCAttributes.SC_CALENDAR_MONTH,
      SCAttributes.SC_CALENDAR_WEEK,
      SCAttributes.SC_CALENDAR_DATE,
    ];
    const attributesHaveTime =
      this.state.attributes.filter(
        x => validTimeDimensions.indexOf(x.id) !== -1
      ).length > 0;
    const dateRange = BuildDateFilter(
      new Date(dateFilter),
      this.state.aggregatorName,
      attributesHaveTime
    );
    this.setState({
      date: dateRange,
      filter,
    });
    const colAttributes = this.state.attributes.filter(
      (x: any) => this.state.columns.indexOf(x.name) > -1
    );
    const rowAttributes = this.state.attributes.filter(
      (x: any) => this.state.rows.indexOf(x.name) > -1
    );

    return {
      aggregationAttributes: colAttributes
        .concat(rowAttributes)
        .map(x => x.id)
        .join(","),
      date: dateRange,
      filter: this.state.filter,
      kpis: this.state.requestKpi,
      sort: `-${this.state.requestKpi}`,
    };
  }

  private pivotOptionsChanged(options: INewPivotTableOptions) {
    this.initCorrelationId();
    let optionsChanged = false;
    const kpi = getAllKpis().filter(
      (x: any) => x.name === options.aggregatorName
    )[0];
    if (
      kpi.id !== this.state.requestKpi ||
      !isEqual(options.rows, this.state.rows) ||
      !isEqual(options.cols, this.state.columns)
    ) {
      this.setState({
        columns: options.cols,
        requestKpi: kpi.id,
        rows: options.rows,
      });
      optionsChanged = true;
      this.fetchAndSetState(this.props);
    }
    if (
      !isEqual(options.aggregatorName, this.state.aggregatorName) ||
      !isEqual(options.rendererName, this.state.renderer)
    ) {
      optionsChanged = true;
      this.setState({
        aggregatorName: kpi.name,
        exclusions: options.exclusions,
        renderer: options.rendererName,
      });
      this.fetchAndSetState(this.props);
    }
    if (
      !isEqual(options.colOrder, this.state.colOrder) ||
      !isEqual(options.rowOrder, this.state.rowOrder)
    ) {
      optionsChanged = true;
      this.setState({
        colOrder: options.colOrder,
        rowOrder: options.rowOrder,
      });
    }

    if (this.props.onConfigChanged && optionsChanged) {
      this.props.onConfigChanged(options);
    }
  }

  private showHideAdvancedMode() {
    this.setState({ advanceShown: !this.state.advanceShown });
  }
  private getChangedAggName(aggregatorName: string) {
    const kpis = getAllKpis().filter((x: any) => x.old_name === aggregatorName);
    return kpis.length > 0 ? kpis[0].name : aggregatorName;
  }
}

export const SalesCreditInteractiveChart = withWidgetConfiguration(
  SalesCreditInteractiveChartClass
);
